import { postToBackend } from "./portalClient";
import { modifyContextFor } from "./hotelModifyAPI";
import { loadPortalSession } from "helpers/portalSession";

/**
 * Flight MANUAL modification and cancellation requests — the backend's
 * modify-request contract (its doc, Quote APIs tab, 30 Oct 2026). ONE family
 * for both kinds: `actionType` "MODIFY" (MRQ-…) or "CANCEL" (CRQ-…).
 *
 *   POST /app/v1/flight/modify-request/create     → { requestId }
 *   POST /app/v1/flight/modify-request/list       → { requests[] }
 *   POST /app/v1/flight/modify-request/quote      → { request }   CRM · open → quoted
 *   POST /app/v1/flight/modify-request/requote    → { request }   CRM · quoted → quoted (new quoteId)
 *   POST /app/v1/flight/modify-request/accept     → { request }   partner / CRM on behalf · quoted → open, round + 1
 *   POST /app/v1/flight/modify-request/reject     → { request }   partner / CRM on behalf · quoted → rejected
 *   POST /app/v1/flight/modify-request/complete   → { request }   CRM · open → completed
 *
 * The backend's `withdraw` is not used: the portal offers no Withdraw yet
 * (useModifyQuoteDialog shows it only when the api has withdrawModifyRequest).
 *
 * An MRQ gets a price quote (amountDue), a CRQ a refund quote (refundAmount)
 * — components/flightServicing/flightQuote. Accepting never finishes a
 * request: it goes back to the CRM as `open` in the next round, one request
 * throughout. app-backend sets userType (and a partner's codes) from the
 * token; the quote calls also send who acts (userId / userEmail).
 */

const BASES = {
  modify: "/app/v1/flight/modify-request",
};
const kindKey = (kind) => (kind === "cancel" || kind === "flight_cancel" ? "cancel" : "modify");
const isCrm = (userType) => String(userType || "").toUpperCase() !== "PARTNER";

/** Both families, listed together on the Requests pages (useAllFlightRequests).
 *  A booking's `list` returns both at once. */
export const kinds = ["modify", "cancel"];

/** Request statuses, in the order a request moves through them. */
export const MODIFY_REQUEST_STATUS = {
  REQUESTED: "requested",
  IN_REVIEW: "in_review",
  QUOTE_READY: "quote_ready",
  QUOTE_APPROVED: "quote_approved",
  QUOTE_REJECTED: "quote_rejected",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
  FAILED: "failed",
};

const contextOf = (booking, currency, userType) =>
  modifyContextFor(booking, currency, userType);

/**
 * Send a manual request → { ok, data: { requestId } }. A quote goes on it
 * afterwards with `sendModifyQuote`.
 *   flights (modify): buildFlightRequestItems · (cancel): buildFlightCancelItems
 *   (Api/flightModifyAPI.js) — [{ transactionId, sectorId, route, departure,
 *   arrival, flightNumber, airline, dates?: { from, to? }, cabin?: { from, to } }]
 *   engineTxnId: select-sectors' modifyTransactionId / cancel/request's
 *   cancelTxnId, when the engine returned one.
 */
export const createModifyRequest = ({ kind, booking, currency, userType, flights, notes, engineTxnId, parentRequestId }) => {
  const cancel = kindKey(kind) === "cancel";
  const body = {
    actionType: cancel ? "CANCEL" : "MODIFY",
    ...contextOf(booking, currency, userType),
    userId: booking?.userId || null,
    itineraryId: booking?.id || null,
    ...(notes ? { notes } : {}),
    ...(engineTxnId ? { [cancel ? "cancelTxnId" : "modifyTransactionId"]: engineTxnId } : {}),
    ...(parentRequestId ? { parentRequestId } : {}),
    flights,
  };
  return postToBackend(`${BASES.modify}/create`, body);
};

/**
 * Every manual request on this booking — modifications and cancellations.
 * `partnerCode` scopes it to the partner, which also leaves out the rows the
 * older flows (select-sectors, cancel/request, ticketing) write without one.
 */
export const listModifyRequests = ({ booking }) => {
  // Always sent ("" = no filter) — see listAllModifyRequests for why.
  const body = { itineraryId: booking?.id || null, partnerCode: String(booking?.partnerCode ?? "").trim() };
  return postToBackend(`${BASES.modify}/list`, body);
};

/**
 * A failed create / list as the operator should read it: the backend's
 * displayMessage, its field-level validationErrors ("flights[0]: …") and the
 * errorReference support needs. The top-level `message` is HTTP client text.
 */
export const requestErrorText = (result, fallback) => {
  const raw = result?.rawError || {};
  const fields = Array.isArray(raw.details?.validationErrors) ? raw.details.validationErrors : [];
  const ref = raw.errorReference || result?.errorReference;
  return [
    raw.displayMessage || result?.displayMessage || fallback,
    fields.length ? fields.join(" · ") : "",
    ref ? `(Ref: ${ref})` : "",
  ]
    .filter(Boolean)
    .join(" — ");
};

/**
 * Every booking's requests, both kinds — the Requests pages and the CRM's
 * sidebar count. `list` without an itineraryId: `{ partnerCode }` for one
 * partner, `partnerCode: ""` for every request (no paging yet).
 *
 * `""`, never left out: app-backend fills every engine body's missing
 * `partnerCode` with the CALLER's own tenant, so a CRM `{}` reached the
 * engine as the admin's house partner and listed none of the partners'
 * requests. The list reads `""` as "no filter", and the caller's value wins.
 */
export const listAllModifyRequests = ({ partnerCode } = {}) =>
  postToBackend(`${BASES.modify}/list`, { partnerCode: String(partnerCode ?? "").trim() });

/** Who acts: the signed-in operator of the calling portal. */
const actorOf = (userType) => {
  const user = loadPortalSession(isCrm(userType) ? "crm" : "partner")?.user || {};
  return { userId: user.id != null ? String(user.id) : null, userEmail: user.email || null };
};

// The common fields of every quote-loop call: the partner context, who acts,
// the booking and the request. The request's id goes as `requestedId`:
// app-backend overwrites a body's `requestId` with its own trace id.
const quoteBody = (booking, currency, userType, { requestId, ...extra }) => ({
  ...contextOf(booking, currency, userType),
  ...actorOf(userType),
  itineraryId: booking?.id || null,
  requestedId: baseRequestId(requestId),
  ...extra,
});

/** A CRM answer for the partner says so, with the reason the dialog asked for. */
const behalfOf = (userType, reason) =>
  isCrm(userType) ? { onBehalf: true, reason } : reason ? { reason } : {};

/** CRM: send a quote on an open request → quoted. */
export const sendModifyQuote = ({ booking, currency, userType, requestId, quote }) =>
  postToBackend(`${BASES.modify}/quote`, quoteBody(booking, currency, userType, { requestId, quote }));

/** CRM: replace the quote the partner is looking at → a new quoteId. A
 *  partner still holding the old one is refused (QUOTE_CHANGED). */
export const updateModifyQuote = ({ booking, currency, userType, requestId, quoteId, quote }) =>
  postToBackend(
    `${BASES.modify}/requote`,
    quoteBody(booking, currency, userType, { requestId, replacesQuoteId: quoteId, quote })
  );

/** CRM: complete an open request — after an accepted quote, or in round 1
 *  with the price unchanged. `paymentMethod` WALLET when an amount is due;
 *  `note` (≤ 500 chars) — e.g. the wallet ledger reference of the debit. */
export const completeModifyRequest = ({ booking, currency, userType, requestId, paymentMethod, note }) =>
  postToBackend(
    `${BASES.modify}/complete`,
    quoteBody(booking, currency, userType, {
      requestId,
      ...(paymentMethod ? { paymentMethod } : {}),
      ...(note ? { note: String(note).slice(0, 500) } : {}),
    })
  );

/** Partner (or CRM on behalf): accept the current quote → back to the CRM, next round. */
export const approveModifyQuote = ({ booking, currency, userType, requestId, quoteId, reason }) =>
  postToBackend(
    `${BASES.modify}/accept`,
    quoteBody(booking, currency, userType, { requestId, quoteId, ...behalfOf(userType, reason) })
  );

/** Partner (or CRM on behalf): reject the current quote → the request closes. */
export const rejectModifyQuote = ({ booking, currency, userType, requestId, quoteId, reason }) =>
  postToBackend(
    `${BASES.modify}/reject`,
    quoteBody(booking, currency, userType, { requestId, quoteId, ...behalfOf(userType, reason) })
  );
/* ── Unwrapping (one extra level of `data` tolerated, as elsewhere) ────── */

const asArray = (v) => (Array.isArray(v) ? v : []);
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

/** A timeline event's wording (the backend sends the event code). */
const EVENT_TEXT = {
  REQUESTED: "Request sent",
  QUOTE_SENT: "Quote sent to the partner",
  QUOTE_SUPERSEDED: "Quote replaced",
  ACCEPTED: "Quote accepted",
  REJECTED: "Quote rejected — request closed",
  COMPLETED: "Request completed",
  COMPLETE_FAILED: "Completion failed — nothing changed",
  WITHDRAWN: "Request withdrawn",
};

/** Who did it, as the timeline shows it. */
const byText = (by) => {
  if (!by || typeof by !== "object") return by || "";
  const who = String(by.userType || "").toUpperCase() === "PARTNER" ? "Partner" : "Zenvoya ops";
  return by.onBehalf ? `${who} (for the partner)` : who;
};

/**
 * A backend timeline event ({ at, event, by: { userId, userType, onBehalf },
 * reason }) as the shared request timeline reads it ({ at, label, by } — `by`
 * is text there; an object would not render).
 */
const toEvent = (e) => ({
  at: e?.at || e?.createdAt || null,
  // Kept to split the timeline into rounds (asRounds).
  round: e?.round == null ? null : num(e.round),
  event: e?.event || null,
  quoteId: e?.quoteId || null,
  data: e?.data || null,
  label: [
    e?.label || EVENT_TEXT[e?.event] || String(e?.event || "").replace(/_/g, " ").toLowerCase(),
    e?.reason,
  ]
    .filter(Boolean)
    .join(" — "),
  by: byText(e?.by),
});

/** The backend's statuses → the ones the shared request UI reads. */
const STATUS_FROM_API = {
  open: MODIFY_REQUEST_STATUS.REQUESTED, // with Zenvoya (round 1, or after an accept)
  quoted: MODIFY_REQUEST_STATUS.QUOTE_READY,
  rejected: MODIFY_REQUEST_STATUS.QUOTE_REJECTED,
  withdrawn: MODIFY_REQUEST_STATUS.CANCELLED,
  in_progress: MODIFY_REQUEST_STATUS.IN_REVIEW, // older flows
};

/** A request with every field the UI reads, defaulted. Its kind comes from
 *  `actionType` (list), else the family it was read from; a cancellation is
 *  stamped "flight_cancel". */
const normalizeQuote = (q) =>
  q
    ? {
        quoteId: q.quoteId || "",
        round: q.round == null ? null : num(q.round),
        version: q.version == null ? null : num(q.version),
        // ACTIVE · SUPERSEDED · ACCEPTED · REJECTED · WITHDRAWN
        status: q.status || null,
        currency: q.currency || "",
        // The admin's fee breakdown (null on older line-based quotes) —
        // modify: the six fees; cancel: bookingPrice + three deductions.
        breakdown:
          q.breakdown && typeof q.breakdown === "object"
            ? Object.fromEntries(Object.entries(q.breakdown).map(([k, v]) => [k, num(v)]))
            : null,
        total: q.total == null ? null : num(q.total),
        previouslyPaid: q.previouslyPaid == null ? null : num(q.previouslyPaid),
        // Refund quotes.
        bookingPrice: q.bookingPrice == null ? null : num(q.bookingPrice),
        totalDeductions: q.totalDeductions == null ? null : num(q.totalDeductions),
        lines: asArray(q.lines).map((l) => ({
          sectorId: l.sectorId || "",
          label: l.label || "",
          oldAmount: num(l.oldAmount),
          newAmount: num(l.newAmount),
          difference: num(l.difference ?? num(l.newAmount) - num(l.oldAmount)),
        })),
        amountDue: num(q.amountDue),
        refundAmount: num(q.refundAmount),
        attachment: q.attachment
          ? {
              name: q.attachment.name || "Attachment",
              contentType: q.attachment.contentType || "",
              size: num(q.attachment.size),
              url: q.attachment.url || null,
              data: q.attachment.data || null,
            }
          : null,
        validUntil: q.validUntil || null,
        note: q.note || "",
        replacesQuoteId: q.replacesQuoteId || null,
        // MODIFY: the quote per leg box × passenger type, as sent ([] on a
        // refund quote or a quote sent without rows).
        fares: asArray(q.fares).map((e) => ({
          sectors: asArray(e?.sectors).map((s) => ({
            sectorId: s?.sectorId || "",
            transactionId: s?.transactionId || "",
            departure: s?.departure || "",
            arrival: s?.arrival || "",
          })),
          paxType: e?.paxType || "",
          count: num(e?.count),
          perPax: Object.fromEntries(Object.entries(e?.perPax || {}).map(([k, v]) => [k, num(v)])),
          total: num(e?.total),
        })),
        raisedBy: q.raisedBy || null,
        createdAt: q.createdAt || null,
        updatedAt: q.updatedAt || null,
      }
    : null;

export const normalizeModifyRequest = (r, kind) =>
  r
    ? {
        kind: kindKey(String(r.actionType || r.kind || kind || "").toLowerCase()) === "cancel" ? "flight_cancel" : "modify",
        requestId: r.requestId || r.id || "",
        parentRequestId: r.parentRequestId || null,
        bookingRef: r.bookingRef || r.itineraryId || "",
        partnerCode: r.partnerCode || null,
        status: (() => {
          const s = String(r.status || "open").toLowerCase();
          return STATUS_FROM_API[s] || s;
        })(),
        round: r.round == null ? null : num(r.round),
        waitingOn: r.waitingOn || null,
        // What the caller may do now — the dialogs show their buttons from it.
        actions: r.actions || null,
        acceptedQuoteId: r.acceptedQuoteId || null,
        currency: r.currency || r.partnerCurrency || "",
        completion: r.completion || null,
        flights: asArray(r.flights),
        reason: r.reason || "",
        notes: r.notes || "",
        requestedBy: r.requestedBy || null,
        createdAt: r.createdAt || null,
        updatedAt: r.updatedAt || r.createdAt || null,
        quote: normalizeQuote(r.quote),
        // Every quote the request was sent, all rounds (asRounds).
        quoteHistory: asArray(r.quoteHistory).map(normalizeQuote).filter(Boolean),
        timeline: asArray(r.timeline).map(toEvent),
      }
    : null;

const payload = (result) => {
  const d = result?.data || {};
  return d.request || d.requests || d.newRequest ? d : d.data || d;
};

export const extractModifyRequest = (result, kind) => normalizeModifyRequest(payload(result).request, kind);

/** `list` also returns the older flows' rows (select-sectors, cancel/request,
 *  ticketing — actionType CHANGE / CANCEL / TICKET, ids like "aB3dE5gH7j").
 *  Only a manual request (MRQ-… / CRQ-…) can be quoted, so only those show. */
const isManualRequest = (r) => /^(MRQ|CRQ)-/.test(String(r?.requestId || ""));

/* ── Rounds ───────────────────────────────────────────────────────────────
   The backend keeps every round on ONE request (round + 1 on each accept) and
   returns only its current quote; the shared request UI lists one quote per
   round of a chain (parentRequestId). So a request in round N reads as N
   rounds: round 1 keeps the requestId, round n is "<requestId> · R<n>" (the
   quote calls send baseRequestId). A past round is quote_approved with the
   last quote sent in it — in full from `quoteHistory` (or the current
   quote), else only its amounts from the timeline's QUOTE_SENT event. */

const ROUND_TAG = " · R";
export const baseRequestId = (id) => String(id || "").split(ROUND_TAG)[0];

/** A quote from its QUOTE_SENT event — the amounts only. */
const quoteFromEvent = (e, currency) => ({
  quoteId: e.quoteId || "",
  round: e.round,
  status: "ACCEPTED",
  currency,
  breakdown: null,
  total: e.data?.total == null ? null : num(e.data.total),
  previouslyPaid: null,
  bookingPrice: null,
  totalDeductions: null,
  lines: [],
  amountDue: num(e.data?.amountDue),
  refundAmount: num(e.data?.refundAmount),
  attachment: null,
  validUntil: null,
  note: "",
  raisedBy: null,
  createdAt: e.at,
  updatedAt: null,
});

const asRounds = (req) => {
  const count = Math.max(1, req.round || 1);
  if (count === 1) return [req];
  // Each event belongs to its round; one without a round follows the last ACCEPTED.
  let at = 1;
  const events = req.timeline.map((e) => {
    const round = e.round || at;
    if (e.event === "ACCEPTED") at = round + 1;
    return { ...e, round: Math.min(round, count) };
  });
  const rounds = [];
  for (let n = 1; n <= count; n += 1) {
    const last = n === count;
    const mine = events.filter((e) => e.round === n);
    // A quote's event: QUOTE_SENT, or QUOTE_SUPERSEDED for a requote (it
    // carries the NEW quote's id).
    const sent = mine.filter((e) => e.quoteId && (e.event === "QUOTE_SENT" || e.event === "QUOTE_SUPERSEDED"));
    const current = req.quote && (req.quote.round || count) === n ? req.quote : null;
    const lastId = sent.length ? sent[sent.length - 1].quoteId : null;
    // The round's last quote: the highest version in quoteHistory.
    const kept =
      req.quoteHistory.filter((q) => q.round === n).sort((a, b) => (a.version || 0) - (b.version || 0)).pop() ||
      req.quoteHistory.find((q) => lastId && q.quoteId === lastId);
    const quote = current || kept || (sent.length ? quoteFromEvent(sent[sent.length - 1], req.currency) : null);
    rounds.push({
      ...req,
      requestId: n === 1 ? req.requestId : `${req.requestId}${ROUND_TAG}${n}`,
      parentRequestId: n > 1 ? rounds[n - 2].requestId : req.parentRequestId,
      status: last ? req.status : MODIFY_REQUEST_STATUS.QUOTE_APPROVED,
      actions: last ? req.actions : {},
      completion: last ? req.completion : null,
      // A requote in the same round reads as an edit of its quote.
      quote: quote && sent.length > 1 ? { ...quote, createdAt: sent[0].at, updatedAt: sent[sent.length - 1].at } : quote,
      timeline: mine,
      // A round starts when the previous round's quote was accepted — also
      // when nothing has happened in it yet, so it still sorts as the newest.
      createdAt:
        n === 1
          ? req.createdAt
          : events.filter((e) => e.round === n - 1 && e.event === "ACCEPTED").pop()?.at || mine[0]?.at || req.updatedAt || req.createdAt,
    });
  }
  return rounds;
};

export const extractModifyRequests = (result, kind) => {
  const p = payload(result);
  const list = Array.isArray(p) ? p : asArray(p.requests);
  return list
    .filter(isManualRequest)
    .map((r) => normalizeModifyRequest(r, kind))
    .filter(Boolean)
    .flatMap(asRounds)
    .sort((a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || "")));
};

export const extractQuoteDecision = (result, kind) => {
  const p = payload(result);
  return {
    request: normalizeModifyRequest(p.request, kind),
    newRequest: normalizeModifyRequest(p.newRequest, kind),
  };
};
